import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Checkbox } from '@/components/ui/Checkbox';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { academicPoliciesApi } from '@/api/config-governance';
import {
  INSTITUTION_TYPES,
  type AcademicPolicy,
  type ChangeEvent,
} from '@/types/config-governance';
import { HistoryTimeline, LifecycleTransitions } from './GovernanceLifecycle';
import styles from './platform-pages.module.css';
import gov from './governance.module.css';

/**
 * One academic policy version: metadata, the rules and exceptions documents,
 * the legal lifecycle moves, and the append-only history.
 *
 * RULES ARE A DOCUMENT, NOT A FORM. The fourteen domains do not share a shape
 * (core stores jsonb and the enforcing product owns each schema), so the
 * editor is a validating JSON textarea: parse on blur, report the offending
 * line and column, refuse to save while unreadable. No heavy editor
 * dependency — the operator writing these documents is writing a contract,
 * and a red line number is what they need.
 *
 * ACTIVE IS READ-ONLY BY DESIGN, not by omission: an ACTIVE version's rules
 * are what a term's decisions were made under, and the single action offered
 * is drafting the next version. The transitions rendered are exactly the
 * machine's legal moves; the reason field is mandatory exactly where the
 * machine demands it (into DRAFT — a rejection).
 */

/**
 * Where JSON.parse choked, as a line/column the operator can go to. V8 and
 * SpiderMonkey word their errors differently ("at position 25" vs "at line 3
 * column 5"), so the position is recomputed from the text when only an offset
 * is given.
 */
export function describeJsonError(text: string, error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const lineColumn = /line (\d+) column (\d+)/.exec(message);
  if (lineColumn) return message;
  const position = /position (\d+)/.exec(message);
  if (position) {
    const offset = Math.min(Number(position[1]), text.length);
    const before = text.slice(0, offset);
    const line = before.split('\n').length;
    const column = offset - before.lastIndexOf('\n');
    return `${message} (line ${line}, column ${column})`;
  }
  return message;
}

export function AcademicPolicyDetailPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { policyId = '' } = useParams();

  const fetcher = useCallback(() => academicPoliciesApi.get(policyId), [policyId]);
  const { state, reload, replace } = useAsyncData<AcademicPolicy>(
    `academic-policy:${policyId}`,
    fetcher,
  );

  const historyFetcher = useCallback(() => academicPoliciesApi.history(policyId), [policyId]);
  const history = useAsyncData<ChangeEvent[]>(`academic-policy-history:${policyId}`, historyFetcher);

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('studio.breadcrumbLabel')}>
        <Link to="/admin/academic-policies">{t('acadPolicies.title')}</Link>
        <Icon name="chevronRight" size={14} aria-hidden="true" />
        <span>
          {state.status === 'ready'
            ? t(`acadPolicies.domains.${state.data.policyDomain}`, {
                defaultValue: state.data.policyDomain,
              })
            : '…'}
        </span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(policy) => (
          <PolicyView
            key={policy.id + policy.updatedAt}
            policy={policy}
            replace={(next) => {
              replace(next);
              history.reload();
            }}
            historyState={history}
            onDeleted={() => navigate('/admin/academic-policies')}
            toast={toast}
          />
        )}
      </AsyncPanel>
    </div>
  );
}

function PolicyView({
  policy,
  replace,
  historyState,
  onDeleted,
  toast,
}: {
  policy: AcademicPolicy;
  replace: (next: AcademicPolicy) => void;
  historyState: ReturnType<typeof useAsyncData<ChangeEvent[]>>;
  onDeleted: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { t } = useTranslation();
  const editable = policy.status === 'DRAFT';
  const [title, setTitle] = useState(policy.title);
  const [description, setDescription] = useState(policy.description ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(policy.effectiveFrom);
  const [changeSummary, setChangeSummary] = useState(policy.changeSummary ?? '');
  const [types, setTypes] = useState<string[]>(policy.applicableInstitutionTypes);
  const [rulesText, setRulesText] = useState(() => JSON.stringify(policy.rules, null, 2));
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [exceptionsText, setExceptionsText] = useState(() =>
    policy.exceptions === null ? '[]' : JSON.stringify(policy.exceptions, null, 2),
  );
  const [exceptionsError, setExceptionsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [redrafting, setRedrafting] = useState(false);
  const rulesId = useId();
  const exceptionsId = useId();

  useEffect(() => {
    setRulesText(JSON.stringify(policy.rules, null, 2));
    setExceptionsText(policy.exceptions === null ? '[]' : JSON.stringify(policy.exceptions, null, 2));
  }, [policy]);

  const checkRules = (): Record<string, unknown> | null => {
    try {
      const parsed: unknown = JSON.parse(rulesText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setRulesError(t('acadPolicies.rulesMustBeObject'));
        return null;
      }
      setRulesError(null);
      return parsed as Record<string, unknown>;
    } catch (cause) {
      setRulesError(describeJsonError(rulesText, cause));
      return null;
    }
  };

  const checkExceptions = (): Record<string, unknown>[] | null => {
    try {
      const parsed: unknown = JSON.parse(exceptionsText);
      if (
        !Array.isArray(parsed) ||
        parsed.some((entry) => typeof entry !== 'object' || entry === null || Array.isArray(entry))
      ) {
        setExceptionsError(t('acadPolicies.exceptionsMustBeArray'));
        return null;
      }
      setExceptionsError(null);
      return parsed as Record<string, unknown>[];
    } catch (cause) {
      setExceptionsError(describeJsonError(exceptionsText, cause));
      return null;
    }
  };

  const save = async () => {
    const rules = checkRules();
    const exceptions = checkExceptions();
    if (rules === null || exceptions === null) return;
    setSaving(true);
    try {
      const updated = await academicPoliciesApi.update(policy.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        effectiveFrom,
        applicableInstitutionTypes: types,
        rules,
        exceptions,
        changeSummary: changeSummary.trim() || undefined,
      });
      replace(updated);
      toast.success(t('acadPolicies.savedToast', { n: updated.version }));
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setDeleteError(null);
    try {
      await academicPoliciesApi.remove(policy.id);
      toast.success(t('acadPolicies.deletedToast', { n: policy.version }));
      onDeleted();
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : String(cause));
      setSaving(false);
    }
  };

  const draftNextVersion = async () => {
    setRedrafting(true);
    try {
      const created = await academicPoliciesApi.create({
        policyDomain: policy.policyDomain,
        title: policy.title,
        description: policy.description ?? undefined,
        effectiveFrom: policy.effectiveFrom,
        applicableInstitutionTypes: policy.applicableInstitutionTypes,
        rules: policy.rules,
        exceptions: policy.exceptions ?? undefined,
        changeSummary: t('acadPolicies.redraftSummary', { n: policy.version }),
        ...(policy.institutionId ? { institutionId: policy.institutionId } : {}),
      });
      toast.success(t('acadPolicies.redraftedToast', { n: created.version }));
      window.location.assign(`/admin/academic-policies/${encodeURIComponent(created.id)}`);
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
      setRedrafting(false);
    }
  };

  const subject = `${policy.policyDomain} v${policy.version}`;

  return (
    <>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{policy.title}</h1>
          <div className={styles.rowMetaLine}>
            <code className={styles.codeTag}>{policy.policyDomain}</code>
            <span className={styles.mutedTag}>{t('grading.versionShort', { n: policy.version })}</span>
            <StateBadge kind="lifecycle" value={policy.status} />
            <span className={gov.scopeTag}>
              {policy.institutionId === null ? t('grading.scopePlatform') : t('grading.scopeInstitution')}
            </span>
          </div>
        </div>
        <div className={gov.inlineActions}>
          {editable && (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setDeleting(true)} disabled={saving}>
                {t('acadPolicies.deleteDraft')}
              </button>
              <button type="button" className={styles.primaryBtn} onClick={save} disabled={saving}>
                {t('acadPolicies.saveDraft')}
              </button>
            </>
          )}
          {(policy.status === 'ACTIVE' || policy.status === 'SUPERSEDED') && (
            <button type="button" className={styles.primaryBtn} onClick={draftNextVersion} disabled={redrafting}>
              <Icon name="plus" size={16} />
              {t('acadPolicies.draftNewVersion')}
            </button>
          )}
        </div>
      </header>

      {policy.status === 'ACTIVE' && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('acadPolicies.readOnlyActive')}
        </p>
      )}
      {policy.status === 'SUPERSEDED' && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('acadPolicies.readOnlySuperseded')}
        </p>
      )}
      {!editable && policy.status !== 'ACTIVE' && policy.status !== 'SUPERSEDED' && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('acadPolicies.readOnlyInReview')}
        </p>
      )}

      <Panel title={t('acadPolicies.lifecyclePanel')} icon="pulse">
        {policy.lastTransitionReason && (
          <p className={styles.rowDetail}>
            {t('governance.history.reason', { reason: policy.lastTransitionReason })}
          </p>
        )}
        <LifecycleTransitions
          status={policy.status}
          subject={subject}
          activationImpact={t('acadPolicies.activateImpact', {
            domain: t(`acadPolicies.domains.${policy.policyDomain}`, {
              defaultValue: policy.policyDomain,
            }),
          })}
          perform={async (to, reason) => {
            const updated = await academicPoliciesApi.transition(policy.id, to, reason);
            replace(updated);
            toast.success(
              t('governance.transition.doneToast', {
                subject,
                status: t(`platform.state.lifecycle.${to}`),
              }),
            );
          }}
        />
        {policy.status === 'SUPERSEDED' && (
          <p className={styles.sectionEmpty} role="status">
            {t('governance.transition.noMoves')}
          </p>
        )}
      </Panel>

      <Panel title={t('acadPolicies.metadataPanel')} icon="settings">
        <div className={gov.metaGrid}>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="policy-title">{t('acadPolicies.fieldTitle')}</label>
            <input id="policy-title" className={gov.editorInput} value={title} disabled={!editable}
              onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="policy-effective">{t('acadPolicies.fieldEffective')}</label>
            <input id="policy-effective" className={gov.editorInputNarrow} type="date" value={effectiveFrom}
              disabled={!editable} onChange={(event) => setEffectiveFrom(event.target.value)} />
          </div>
        </div>
        <div className={gov.fieldStack} style={{ marginTop: '0.75rem' }}>
          <label className={styles.fieldLabel} htmlFor="policy-description">{t('acadPolicies.fieldDescription')}</label>
          <textarea id="policy-description" className={styles.textarea} rows={2} value={description}
            disabled={!editable} onChange={(event) => setDescription(event.target.value)} />
        </div>
        <div className={gov.fieldStack} style={{ marginTop: '0.75rem' }}>
          <label className={styles.fieldLabel} htmlFor="policy-summary">{t('acadPolicies.fieldSummary')}</label>
          <textarea id="policy-summary" className={styles.textarea} rows={2} value={changeSummary}
            disabled={!editable} onChange={(event) => setChangeSummary(event.target.value)} />
        </div>
        <fieldset className={styles.fieldset} style={{ marginTop: '0.75rem' }}>
          <legend className={styles.fieldLabel}>{t('grading.fieldTypes')}</legend>
          <p className={styles.fieldHint}>{t('grading.fieldTypesHint')}</p>
          <div className={gov.checkboxGrid}>
            {INSTITUTION_TYPES.map((type) => (
              <Checkbox
                key={type}
                label={t(`admin.profile.institutionTypes.${type}`, { defaultValue: type })}
                checked={types.includes(type)}
                disabled={!editable}
                onChange={(event) =>
                  setTypes(
                    event.target.checked
                      ? [...types, type]
                      : types.filter((value) => value !== type),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
      </Panel>

      <Panel title={t('acadPolicies.rulesPanel')} icon="admin">
        <p className={styles.fieldHint} id={`${rulesId}-hint`}>
          {t('acadPolicies.rulesHint')}
        </p>
        <textarea
          id={rulesId}
          className={`${gov.jsonArea} ${rulesError ? gov.jsonAreaInvalid : ''}`}
          value={rulesText}
          readOnly={!editable}
          spellCheck={false}
          aria-label={t('acadPolicies.rulesPanel')}
          aria-describedby={`${rulesId}-hint`}
          aria-invalid={rulesError !== null}
          onChange={(event) => setRulesText(event.target.value)}
          onBlur={checkRules}
        />
        {rulesError && (
          <p className={gov.jsonError} role="alert">
            {rulesError}
          </p>
        )}

        <p className={styles.fieldHint} id={`${exceptionsId}-hint`} style={{ marginTop: '0.75rem' }}>
          {t('acadPolicies.exceptionsHint')}
        </p>
        <textarea
          id={exceptionsId}
          className={`${gov.jsonArea} ${exceptionsError ? gov.jsonAreaInvalid : ''}`}
          value={exceptionsText}
          readOnly={!editable}
          spellCheck={false}
          aria-label={t('acadPolicies.exceptionsLabel')}
          aria-describedby={`${exceptionsId}-hint`}
          aria-invalid={exceptionsError !== null}
          onChange={(event) => setExceptionsText(event.target.value)}
          onBlur={checkExceptions}
        />
        {exceptionsError && (
          <p className={gov.jsonError} role="alert">
            {exceptionsError}
          </p>
        )}
      </Panel>

      <Panel title={t('governance.history.title')} icon="history">
        <AsyncPanel state={historyState.state} onRetry={historyState.reload}>
          {(events) => <HistoryTimeline events={events} />}
        </AsyncPanel>
      </Panel>

      <ConfirmActionDialog
        open={deleting}
        title={t('acadPolicies.deleteTitle', { n: policy.version })}
        intro={t('acadPolicies.deleteIntro')}
        tone="critical"
        impactStatus="ready"
        impact={<p className={styles.rowDetail}>{t('acadPolicies.deleteImpact')}</p>}
        confirmPhrase={policy.policyDomain}
        confirmLabel={t('acadPolicies.deleteDraft')}
        submitting={saving}
        error={deleteError}
        onCancel={() => {
          setDeleting(false);
          setDeleteError(null);
        }}
        onConfirm={() => void remove()}
      />
    </>
  );
}
